import { AnalyticCardEmptyState } from '../../EmptyState/AnalyticCardEmptyState.tsx';
import { PaperWithUser } from '@vy/ui-design-system';
import { AnalyticCard } from '../AnalyticCard/AnalyticCard.tsx';
import { Box, Stack } from '@mui/material';
import { AnalyticMetricV2 } from '../AnalyticMetric/AnalyticMetricV2.tsx';
import { formatPriceToAbbreviated } from '../../../../../utils/formatters/formatPriceToAbbreviated.ts';
import { DEFAULT_CURRENCY } from '@vy/types';
import { isNullOrUndefined } from '@vy/utils';
import type { KpiMrrWaterfall } from '@vy/api-definition';
import { MMRBreakdown, type MMRBreakdownRow } from './MMRBreakdown.tsx';
import dayjs from 'dayjs';
import { homePageWidgetsSelectors } from '@vy/app-selectors';

type MRRWaterfallAnalyticsCardProps = {
  waterfall: KpiMrrWaterfall | undefined;
  isLoading: boolean;
};

export const MRRWaterfallAnalyticsCard = ({ waterfall, isLoading }: MRRWaterfallAnalyticsCardProps) => {
  const beginningMrr = waterfall?.beginningMrr ?? 0;
  const endingMrr = waterfall?.endingMrr ?? 0;
  const nrr = waterfall?.nrr ?? 0;
  const grr = waterfall?.grr ?? 0;
  const monthLabel = waterfall?.month ? dayjs(waterfall.month).format('MMMM YYYY') : 'Last month';

  const isNoData = isNullOrUndefined(waterfall) || (beginningMrr === 0 && endingMrr === 0);
  const breakdownData = buildBreakdownData(waterfall, beginningMrr);

  return (
    <AnalyticCard
      title="MRR Waterfall"
      dataVy={homePageWidgetsSelectors.mrrWaterfallWidget}
      isLoading={isLoading}
    >
      {isNoData ? (
        <AnalyticCardEmptyState
          title="No MMR Waterfall yet"
          description="Unable to display data"
          icon={<PaperWithUser />}
        />
      ) : (
        <Stack direction="row" sx={{
          justifyContent: "space-between"
        }}>
          <Stack
            sx={{
              gap: 2.5,
              flexGrow: 1
            }}>
            <AnalyticMetricV2
              titleText={`MRR (${monthLabel})`}
              value={endingMrr}
              formatter={(value) => formatPriceToAbbreviated(DEFAULT_CURRENCY, value)}
              prevValue={beginningMrr}
              growthRateLabel="MoM"
              dataVy={homePageWidgetsSelectors.mrrWaterfallMrr}
            />
            <AnalyticMetricV2
              titleText="NRR/GRR"
              value={nrr}
              formatter={nrrFormatter(grr)}
              valueFontSize={20}
              titleSx={{
                marginBottom: '6px',
              }}
              dataVy={homePageWidgetsSelectors.mrrWaterfallNrrGrr}
            />
          </Stack>
          <Box sx={{
            flexGrow: 2
          }}>
            <MMRBreakdown
              data={breakdownData}
              endingMrr={endingMrr}
              endingMrrDataVy={homePageWidgetsSelectors.mrrWaterfallEndingMrr}
            />
          </Box>
        </Stack>
      )}
    </AnalyticCard>
  );
};

function buildBreakdownData(
  waterfall: KpiMrrWaterfall | undefined,
  beginningMrr: number,
): MMRBreakdownRow[] {
  if (isNullOrUndefined(waterfall)) {
    return [];
  }

  return [
    {
      label: 'Beginning MRR',
      value: beginningMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallBeginningMrr,
    },
    {
      label: '+ New',
      value: waterfall.movements.newMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallNewMrr,
    },
    {
      label: '+ Reactivation',
      value: waterfall.movements.reactivationMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallReactivationMrr,
    },
    {
      label: '+ Expansion',
      value: waterfall.movements.expansionMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallExpansionMrr,
    },
    {
      label: '- Contraction',
      value: waterfall.movements.contractionMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallContractionMrr,
    },
    {
      label: '- Churned',
      value: waterfall.movements.churnMrr,
      dataVy: homePageWidgetsSelectors.mrrWaterfallChurnMrr,
    },
  ];
}

function nrrFormatter(grr: number) {
  return (value: number) => `${value}% / ${grr}%`;
}
